import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import { categoryMeta } from '@/mocks/content';
import { formatDate, formatViews, getById, getRelated } from '@/utils/content';

const shareTargets = [
  { key: 'wechat', label: '微信', icon: 'ri-wechat-line' },
  { key: 'weibo', label: '微博', icon: 'ri-weibo-line' },
  { key: 'link', label: '复制链接', icon: 'ri-link' },
];

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getById(id) : undefined;
  const [toast, setToast] = useState<string | null>(null);

  useDocumentTitle(
    item ? `${item.title} - ${item.categoryName} - 小` : '内容未找到 - 小',
    item?.summary,
    item ? `${item.tag},${item.title},小` : undefined
  );

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="mx-auto w-full max-w-[900px] px-4 py-16 md:px-6">
        <EmptyState
          title="内容不存在或已被移除"
          description="你访问的内容可能已经下线，返回首页看看其他精彩内容吧。"
        />
        <div className="mt-6 flex justify-center">
          <Link
            to="/"
            className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const meta = categoryMeta[item.category];
  const related = getRelated(item);

  return (
    <article className="mx-auto w-full max-w-[900px] px-4 py-8 md:px-6 md:py-10">
      <div className="mb-5">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: `${item.categoryName}频道`, to: meta.path },
            { label: item.tag },
          ]}
        />
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="rounded bg-accent-500 px-2.5 py-1 text-xs font-semibold text-background-50 dark:text-foreground-950">
          {item.categoryName} · {item.tag}
        </span>
        {item.featured && (
          <span className="flex items-center gap-1 rounded bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
            <i className="ri-bookmark-3-line"></i>
            精选内容
          </span>
        )}
      </div>

      <h1 className="mb-5 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
        {item.title}
      </h1>

      <div className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200/70 pb-5 text-sm text-foreground-600">
        <span className="flex items-center gap-1.5">
          <i className="ri-user-line"></i>
          {item.author}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-calendar-line"></i>
          {formatDate(item.date)}
        </span>
        <span className="flex items-center gap-1.5">
          <i className="ri-fire-line"></i>
          {formatViews(item.views)} 阅读
        </span>
      </div>

      <figure className="mb-8">
        <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
          <img
            src={item.image}
            alt={item.title}
            title={`${item.title} - ${item.tag}`}
            className="h-full w-full object-cover"
          />
        </div>
        <figcaption className="mt-3 text-center text-xs text-foreground-500">
          图片来源：小 · 内容中心（演示素材）
        </figcaption>
      </figure>

      <p className="mb-6 rounded-lg border-l-4 border-accent-500 bg-background-50 px-5 py-4 text-sm leading-relaxed text-foreground-700">
        {item.summary}
      </p>

      <div className="space-y-5 text-base leading-loose text-foreground-800">
        {item.body.map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-2">
        <span className="text-sm text-foreground-600">相关标签：</span>
        <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
          #{item.tag}
        </span>
        <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
          #{item.categoryName}
        </span>
      </div>

      {/* 分享 */}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200/70 bg-background-50 px-5 py-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground-800">
          <i className="ri-share-forward-line text-primary-600"></i>
          分享这篇内容
          <span className="rounded bg-background-200 px-2 py-0.5 text-xs font-normal text-foreground-600">演示</span>
        </div>
        <div className="flex items-center gap-2">
          {shareTargets.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setToast(`已模拟分享到${s.label}（演示功能，暂无实际分享）`)}
              className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className={s.icon}></i>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 分类入口 */}
      <Link
        to={meta.path}
        className="mt-6 flex items-center justify-between rounded-lg bg-primary-900 px-5 py-4 text-background-100 transition-colors hover:bg-primary-950"
      >
        <span className="flex items-center gap-3">
          <i className={`${meta.icon} text-2xl text-accent-400`}></i>
          <span>
            <span className="block text-sm font-semibold text-background-50">浏览更多{meta.name}内容</span>
            <span className="block text-xs text-background-300">{meta.desc}</span>
          </span>
        </span>
        <i className="ri-arrow-right-line text-xl"></i>
      </Link>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-links-line text-primary-600"></i>
            相关推荐
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {related.map((rel) => (
              <Link
                key={rel.id}
                to={`/detail/${rel.id}`}
                className="group flex gap-4 rounded-lg border border-background-200/70 bg-background-50 p-4 transition-all duration-300 hover:-translate-y-1 hover:border-primary-200"
              >
                <div className="h-20 w-24 flex-shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={rel.image}
                    alt={rel.title}
                    title={`${rel.title} - ${rel.tag}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="mb-1 inline-block rounded bg-background-100 px-2 py-0.5 text-xs text-foreground-600">
                    {rel.tag}
                  </span>
                  <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
                    {rel.title}
                  </h3>
                  <p className="mt-1.5 line-clamp-1 text-xs text-foreground-500">{rel.summary}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-foreground-950 px-5 py-3 text-sm text-background-50">
          {toast}
        </div>
      )}
    </article>
  );
}